import React, { useState, useEffect } from 'react';
import { orderAPI } from '../services/api';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Collapse,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
  Badge,
  CircularProgress
} from '@mui/material';
import {
  ShoppingBag,
  ArrowBack,
  FilterList,
  Sort,
  Search,
  ExpandMore,
  ExpandLess,
  LocalShipping,
  CheckCircle,
  Pending,
  Cancel,
  Schedule,
  Payment,
  Receipt,
  Info,
  Warning,
  Store,
  Home,
  AccessTime,
  CalendarToday,
  AccountCircle,
  Phone,
  LocationOn,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNavigation';
import BrandLoader from '../components/BrandLoader';
import useResponsive from '../hooks/useResponsive';
import { formatCurrency } from '../utils/countryUtils';
import { formatDateShort } from '../utils/timezoneUtils';

const ProductOrderListing = () => {
  const navigate = useNavigate();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [showFilters, setShowFilters] = useState(false);

  // Helper function to format shipping address
  const formatShippingAddress = (shippingAddress) => {
    if (!shippingAddress) return 'Address not available';
    
    // If it's a string (legacy format), return as is
    if (typeof shippingAddress === 'string') {
      return shippingAddress;
    }
    
    // If it's an object, format the address
    const { street, city, state, postcode, country } = shippingAddress;
    
    if (!street) return 'Address not available';
    
    const addressParts = [street];
    if (city) addressParts.push(city);
    if (state && postcode) addressParts.push(`${state} ${postcode}`);
    if (country && country !== 'Australia') addressParts.push(country);
    
    return addressParts.join(', ');
  };
  const [sortBy, setSortBy] = useState('createdAt');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // State for orders data
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Fetch orders from API (no pagination - fetch all orders)
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortBy
      };

      const response = await orderAPI.getOrders(params);
      
      if (response.success) {
        setOrders(response.data.orders || []);
      } else {
        setError(response.error || 'Failed to load orders');
      }
    } catch (error) {
      setError(error.error || 'Failed to load orders. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  // Fetch orders when component mounts or filters change
  useEffect(() => {
    fetchOrders();
  }, [statusFilter, sortBy]);


  const statusFilters = [
    { id: 'all', label: 'All Orders' },
    { id: 'pending', label: 'Pending' },
    { id: 'confirmed', label: 'Confirmed' },
    { id: 'processing', label: 'Processing' },
    { id: 'shipped', label: 'Shipped' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'cancelled', label: 'Cancelled' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#FFA500';
      case 'confirmed':
        return '#007AFF';
      case 'processing':
        return '#FF6B35';
      case 'shipped':
        return '#4CAF50';
      case 'delivered':
        return '#2E7D32';
      case 'cancelled':
        return '#D32F2F';
      default:
        return '#666';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'confirmed':
        return 'Confirmed';
      case 'processing':
        return 'Processing';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const handleOrderClick = (order) => {
    navigate(`/product-order/${order._id}`);
  };

  const handleCancel = (order) => {
    setSelectedOrder(order);
    setShowCancelDialog(true);
  };

  const handleBack = () => {
    navigate('/profile');
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      setActionLoading(true);
      const response = await orderAPI.cancelOrder(selectedOrder._id, {
        reason: 'Customer requested cancellation'
      });
      
      if (response.success) {
        // Refresh orders list
        await fetchOrders();
        setShowCancelDialog(false);
        setSelectedOrder(null);
        // Show success message
        alert('Order cancelled successfully');
      } else {
        alert('Failed to cancel order: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to cancel order: ' + (error.error || 'Please try again.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleTrackOrder = (order) => {
    if (order.trackingNumber) {
      // Open tracking URL or show tracking info
      alert(`Tracking Number: ${order.trackingNumber}`);
    } else {
      alert('Tracking information is not available yet');
    }
  };

  // Calculate total quantity of items in an order
  const calculateTotalQuantity = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => total + (item.quantity || 0), 0);
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesSearch = order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.items?.some(item => item.productName?.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'total':
        return (b.totalAmount || 0) - (a.totalAmount || 0);
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  const OrderCard = ({ order }) => (
    <Card 
      sx={{ 
        mb: 1.5, 
        cursor: 'pointer',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease',
          borderColor: '#007AFF'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${getStatusColor(order.status)} 0%, #007AFF 100%)`,
          zIndex: 1
        }
      }}
      onClick={() => handleOrderClick(order)}
    >
      <CardContent sx={{ p: 2, pt: 2.5 }}>
        {/* Header with Order Number and Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt sx={{ fontSize: 16, color: '#007AFF' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }}>
              #{order.orderNumber}
            </Typography>
          </Box>
          <Chip
            label={getStatusText(order.status)}
            size="small"
            sx={{
              backgroundColor: getStatusColor(order.status),
              color: 'white',
              fontWeight: 600,
              fontSize: '0.65rem',
              height: 20
            }}
          />
        </Box>

                 {/* Combined Date, Items Count, and Total */}
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
             <CalendarToday sx={{ fontSize: 12, color: '#666' }} />
             <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
               {formatDateShort(order.createdAt)}
             </Typography>
           </Box>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <ShoppingBag sx={{ fontSize: 12, color: '#666' }} />
             <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
               {calculateTotalQuantity(order.items)} items
             </Typography>
           </Box>
           <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#007AFF', fontSize: '0.8rem' }}>
             {formatCurrency(order.totalAmount || 0)}
           </Typography>
         </Box>

                 {/* Items Summary */}
         <Box sx={{ mb: 1 }}>
                                                 <Typography variant="caption" sx={{ color: '#333', lineHeight: 1.2, fontSize: '0.7rem' }}>
                {order.items?.slice(0, 2).map(item => item.productName || 'Product').join(', ')}
                {order.items?.length > 2 && ` +${order.items.length - 2} more`}
              </Typography>
         </Box>

        {/* Combined Delivery Address */}
        <Box sx={{ mb: 1 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
             <LocationOn sx={{ fontSize: 12, color: '#007AFF' }} />
                          <Typography variant="caption" sx={{ color: '#333', lineHeight: 1.2, fontSize: '0.7rem' }}>
                {formatShippingAddress(order.shippingAddress)}
              </Typography>
           </Box>
        </Box>

                 {/* Tracking Status Only */}
         {order.trackingNumber && (
           <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
               <LocalShipping sx={{ color: '#007AFF', fontSize: 12 }} />
               <Typography variant="caption" sx={{ color: '#007AFF', fontSize: '0.65rem' }}>
                 Tracked
               </Typography>
             </Box>
           </Box>
         )}

        {/* Design Element - Status Indicator Line */}
        <Box 
          sx={{ 
            position: 'absolute', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            height: '2px', 
            background: getStatusColor(order.status),
            opacity: 0.3
          }} 
        />
      </CardContent>
    </Card>
  );

  const OrderDetailsScreen = () => (
    <Dialog
      open={showOrderDetails}
      onClose={() => setShowOrderDetails(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Order #{selectedOrder?.orderNumber}</Typography>
          <Chip
            label={getStatusText(selectedOrder?.status)}
            sx={{
              backgroundColor: getStatusColor(selectedOrder?.status),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedOrder && (
          <Box>
            {/* Order Date */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Order Date: {formatDateShort(selectedOrder.createdAt)}
              </Typography>
              {selectedOrder.estimatedDelivery && (
                <Typography variant="body2" color="text.secondary">
                  Estimated Delivery: {formatDateShort(selectedOrder.estimatedDelivery)}
                </Typography>
              )}
            </Box>

            {/* Order Items */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Order Items
              </Typography>
              {selectedOrder.items.map((item) => (
                <Card key={item._id} sx={{ mb: 2, backgroundColor: '#f8f9fa' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', flex: 1 }}>
                        {item.productName}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#007AFF' }}>
                        {formatCurrency(item.price || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Qty: {item.quantity}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(item.total || 0)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Order Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Order Summary
              </Typography>
              <Box sx={{ backgroundColor: '#f8f9fa', p: 2, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">{formatCurrency(selectedOrder.subtotal || 0)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Shipping Fee:</Typography>
                  <Typography variant="body2">{formatCurrency(selectedOrder.shipping || 0)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">GST:</Typography>
                  <Typography variant="body2">{formatCurrency(selectedOrder.tax || 0)}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total:
                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#007AFF' }}>
                  {formatCurrency(selectedOrder.totalAmount || 0)}
                </Typography>
                </Box>
              </Box>
            </Box>

            {/* Shipping Address */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Shipping Address
              </Typography>
              <Card sx={{ backgroundColor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {selectedOrder.shippingAddress?.name || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {selectedOrder.shippingAddress?.phone || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatShippingAddress(selectedOrder.shippingAddress)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Payment Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Payment Information
              </Typography>
              <Card sx={{ backgroundColor: '#f8f9fa' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedOrder.paymentMethod}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: selectedOrder.paymentStatus === 'completed' ? '#4CAF50' : '#FFA500'
                      }}
                    >
                      {selectedOrder.paymentStatus === 'completed' ? 'Paid' : 'Pending'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Notes */}
            {selectedOrder.notes && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Notes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedOrder.notes}
                </Typography>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {selectedOrder.status === 'pending' && (
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  onClick={() => {
                    setShowOrderDetails(false);
                    handleCancel(selectedOrder);
                  }}
                >
                  Cancel Order
                </Button>
              )}
              
              {selectedOrder.trackingNumber && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => handleTrackOrder(selectedOrder)}
                >
                  Track Order
                </Button>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );

  const CancelDialog = () => (
    <Dialog open={showCancelDialog} onClose={() => !actionLoading && setShowCancelDialog(false)}>
      <DialogTitle>Cancel Order</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to cancel this order? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => setShowCancelDialog(false)} 
          disabled={actionLoading}
        >
          No
        </Button>
        <Button 
          onClick={handleCancelOrder} 
          color="error" 
          variant="contained"
          disabled={actionLoading}
          startIcon={actionLoading ? <BrandLoader size="small" showText={false} /> : null}
        >
          {actionLoading ? 'Cancelling...' : 'Yes, Cancel Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ pb: 8, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Loading State */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '200px',
          flexDirection: 'column',
          gap: 2
        }}>
          <BrandLoader />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '200px',
          flexDirection: 'column',
          gap: 2
        }}>
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
            <Button 
              onClick={fetchOrders} 
              sx={{ mt: 1 }}
              variant="outlined"
            >
              Try Again
            </Button>
          </Alert>
        </Box>
      )}
      
      {/* Content - Only show when not loading and has loaded at least once */}
      {!loading && !error && hasLoaded && (
        <>
          {/* Header */}
          <Box sx={{ 
            backgroundColor: '#0A2A66', 
            color: 'white', 
            p: 2,
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={handleBack} sx={{ color: 'white' }}>
                  <ArrowBack />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  My Orders
                </Typography>
              </Box>
              <IconButton
                onClick={fetchOrders}
                sx={{ color: 'white' }}
                disabled={loading}
              >
                <Refresh sx={{ transform: loading ? 'rotate(360deg)' : 'none', transition: 'transform 0.5s' }} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ 
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            width: '100%',
            backgroundColor: '#f5f5f5',
            mt: 2 
          }}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterList />
            </IconButton>
          </Box>

          <Collapse in={showFilters}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="createdAt">Date</MenuItem>
                  <MenuItem value="totalAmount">Total</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  {statusFilters.map((filter) => (
                    <MenuItem key={filter.id} value={filter.id}>
                      {filter.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Collapse>
        </Paper>

        {/* Orders List */}
        {sortedOrders.length > 0 ? (
          <Box>
            {sortedOrders.map((order) => (
              <OrderCard key={order._id} order={order} />
            ))}
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            py: 8 
          }}>
            <ShoppingBag sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              No orders found
            </Typography>
                         <Typography variant="body2" sx={{ color: '#999', textAlign: 'center' }}>
               {statusFilter === 'all' 
                 ? "No product orders placed" 
                 : `No ${statusFilter} orders found`}
             </Typography>
            {statusFilter === 'all' && (
              <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => navigate('/')}
              >
                Start Shopping
              </Button>
            )}
          </Box>
        )}
        </Box>
        </>
      )}


      {/* Bottom Navigation */}
      <BottomNav />

      {/* Dialogs */}
      <OrderDetailsScreen />
      <CancelDialog />
    </Box>
  );
};

export default ProductOrderListing; 